import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  createDocumentsForCession,
  getCessionsToCreateNominee,
} from '../../../../../../../lib/api/reverseCession';
import EnhancedTable from '../../../../../../../lib/designSystem/Table';
import TableWithPagination from '../../../../../../../lib/designSystem/TableWithPagination';
import dayjs from 'dayjs';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import GlobalSearcher, {
  Filter,
} from '../../../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import { showAlert } from '../../../../../../../lib/functions/utils';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'folio', label: 'Folio' },
  { key: 'docType', label: 'Tipo' },
  { key: 'businessName', label: 'Cliente' },
  { key: 'businessIdentifier', label: 'RUT' },
  { key: 'amount', label: 'Monto Documento', type: 'money' },
  { key: 'issuedDate', label: 'Emisión', type: 'date' },
  { key: 'cessionDate', label: 'Cesion', type: 'date' },
];
const NewReversedCessionsNomineeDialog = (props: any) => {
  const {
    open,
    setOpen,
    setDocumentsIdsForCessionDialog,
    setOpenCessionDialog,
  } = props;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [cessions, setCessions] = useState([]);
  const [cessionsToCreateNominee, setCessionsToCreateNominee] = useState([]);
  const startDateDefault = new Date();
  startDateDefault.setMonth(startDateDefault.getMonth() - 1);
  const [startDate, setStartDate] = useState(dayjs(startDateDefault));
  const [endDate, setEndDate] = useState(dayjs(new Date()));
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [folio, setFolio] = useState('');
  const [cessionaryIdentifier, setCessionaryIdentifier] = useState('');
  const [selectedCessions, setSelectedCessions] = useState<string[]>([]);
  const PAGE_SIZE = 5;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (cessionaryIdentifier)
      query += `cessionaryIdentifier=${cessionaryIdentifier}&`;
    if (folio) query += `folio=${folio}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchCessionsToCreateNominee = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoading(true);
      const query = generateQuery({
        page: page,
        pageSize: pageSize,
      });
      const response = await getCessionsToCreateNominee(dispatch, query);
      setCessions(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha cesion inicio',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      type: 'date',
      label: 'Fecha cesion fin',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      type: 'text',
      label: 'Folio',
      value: folio,
      setter: setFolio,
    },
    {
      type: 'text',
      label: 'RUT emisor',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'RUT deudor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      type: 'text',
      label: 'RUT cesionario',
      value: cessionaryIdentifier,
      setter: setCessionaryIdentifier,
    },
  ];

  const addCessionsToCreateNominee = () => {
    const newCessionsToCreateNominee = cessions.filter((cession: any) =>
      selectedCessions.includes(cession.id),
    );
    setSelectedCessions([]);
    setCessionsToCreateNominee([
      ...cessionsToCreateNominee,
      ...newCessionsToCreateNominee,
    ]);
  };

  const cantSelectRow = (row: any) => {
    return cessionsToCreateNominee.some(
      (cession: any) => cession.id === row.id,
    );
  };

  const handleGoToCessionDialog = async () => {
    setLoading(true);
    try {
      const response = await createDocumentsForCession(dispatch, {
        documents: cessionsToCreateNominee,
      });
      setDocumentsIdsForCessionDialog(response);
      console.log(response);
      setOpenCessionDialog(true);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al seleccionar documentos');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCessionsToCreateNominee({ page: 1, pageSize: PAGE_SIZE });
  }, []);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Reverso de cesiones</DialogTitle>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <div>
          <DialogContent>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0px',
              }}
            >
              <Typography variant="body1">
                Selecciona las cesiones a crear nómina
              </Typography>
              <div style={{ display: 'flex', gap: '8px' }}>
                <GlobalSearcher
                  filters={filters}
                  triggerSearch={() =>
                    fetchCessionsToCreateNominee({ page: 1, pageSize: 10 })
                  }
                />
                <CustomButton onClick={() => addCessionsToCreateNominee()}>
                  Agregar
                </CustomButton>
              </div>
            </div>

            <TableWithPagination
              dataHeaders={headers}
              data={cessions}
              setData={setCessions}
              pagination={pagination}
              setPagination={setPagination}
              searchData={fetchCessionsToCreateNominee}
              selected={selectedCessions}
              setSelected={setSelectedCessions}
              cantSelectRow={cantSelectRow}
            />
            <div style={{ marginTop: 20 }}>
              <Typography variant="body1">Cesiones seleccionadas</Typography>
              <EnhancedTable
                data={cessionsToCreateNominee}
                dataHeaders={headers}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <CustomButton color="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </CustomButton>
            <CustomButton
              disabled={cessionsToCreateNominee.length === 0}
              onClick={() => handleGoToCessionDialog()}
            >
              Avanzar
            </CustomButton>
          </DialogActions>
        </div>
      )}
      {/* <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar reverso</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de querer crear el reverso de cesiones?
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </CustomButton>
          <CustomButton
            disabled={cessionsToCreateNominee.length === 0}
            onClick={() => handleGoToCessionDialog()}
          >
            Avanzar
          </CustomButton>
        </DialogActions>
      </Dialog> */}
    </Dialog>
  );
};

export default NewReversedCessionsNomineeDialog;
